import { createPortal } from "react-dom";
import ProductSearchDropdown from "./ProductSearchDropdown";

const inputClass =
  "w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm text-black placeholder-gray-400 focus:outline-none focus:border-black focus:bg-white transition-all duration-200";

const labelClass =
  "block text-xs font-semibold text-gray-500 uppercase tracking-widest mb-2";

const BannerEditModal = ({
  open,
  onClose,
  bannerData,
  handleImageChange,
  handleChange,
  handleSubmit,
  productSearch,
  productResults,
  loadingProducts,
  handleProductInputChange,
  handleSelectProduct,
}) => {
  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" />

      {/* Modal card */}
      <div className="relative w-full max-w-lg bg-white rounded-3xl shadow-2xl overflow-hidden max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="bg-black px-8 py-6 flex items-center justify-between flex-shrink-0">
          <div>
            <h2 className="text-white text-lg font-bold">Edit Banner</h2>
            <p className="text-gray-400 text-xs mt-0.5">Update banner details below</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Scrollable body */}
        <div className="px-8 py-6 overflow-y-auto space-y-5">
          {/* Start date */}
          <div>
            <label className={labelClass}>Start Date & Time</label>
            <input
              type="datetime-local"
              value={bannerData.startDate}
              onChange={(e) => handleChange("startDate", e.target.value)}
              className={inputClass}
            />
          </div>

          {/* End date */}
          <div>
            <label className={labelClass}>End Date & Time</label>
            <input
              type="datetime-local"
              value={bannerData.endDate}
              onChange={(e) => handleChange("endDate", e.target.value)}
              className={inputClass}
            />
          </div>

          {/* Description */}
          <div>
            <label className={labelClass}>Description</label>
            <input
              type="text"
              value={bannerData.description}
              onChange={(e) => handleChange("description", e.target.value)}
              placeholder="Enter banner description"
              className={inputClass}
            />
          </div>

          {/* Product search */}
          <ProductSearchDropdown
            productSearch={productSearch}
            productResults={productResults}
            loadingProducts={loadingProducts}
            productId={bannerData.productId}
            onInputChange={handleProductInputChange}
            onSelect={handleSelectProduct}
          />

          {/* Image upload */}
          <div>
            <label className={labelClass}>Replace Image (Optional)</label>
            <input
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              className="block w-full text-sm text-gray-600 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-black file:text-white hover:file:bg-gray-800 cursor-pointer"
            />
            {bannerData.previewUrl && (
              <img
                src={bannerData.previewUrl}
                alt="Preview"
                className="mt-3 w-full rounded-xl border border-gray-200 object-cover"
              />
            )}
          </div>

          {/* Visible toggle */}
          <label className="flex items-center gap-3 cursor-pointer group">
            <div
              onClick={() => handleChange("visible", !bannerData.visible)}
              className={`w-4 h-4 border-2 rounded transition-all duration-200 flex items-center justify-center cursor-pointer ${
                bannerData.visible
                  ? "bg-black border-black"
                  : "bg-white border-gray-300 group-hover:border-gray-500"
              }`}
            >
              {bannerData.visible && (
                <svg className="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                </svg>
              )}
            </div>
            <span className="text-sm text-gray-600 group-hover:text-black transition-colors select-none">
              Visible
            </span>
          </label>
        </div>

        {/* Footer */}
        <div className="px-8 py-5 border-t border-gray-100 flex-shrink-0">
          <button
            onClick={handleSubmit}
            className="w-full bg-blue-600 text-white py-3.5 rounded-xl font-semibold text-sm tracking-wide hover:bg-blue-700 active:scale-[0.98] transition-all duration-200"
          >
            Save Changes
          </button>
          <button
            type="button"
            onClick={onClose}
            className="w-full mt-3 py-3 rounded-xl text-sm text-gray-500 hover:text-black font-medium border border-gray-200 hover:border-gray-400 transition-all duration-200"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default BannerEditModal;
